/**
 * نشان‌کردن روی سایت — قرینه‌ی `FavDot`ِ موبایل
 * (apps/mobile/src/features/discover/cards.tsx).
 *
 * تا حالا فقط گوشی این دکمه را داشت و سایت هیچ راهی برای نشان کردن نمی‌داد؛
 * فهرست‌ها نشان‌شده‌ها را بالا می‌آوردند ولی خودِ کار فقط روی گوشی ممکن بود.
 *
 * یک هوک برای همه‌ی صفحه (نه یکی به‌ازای هر کارت): با پنجاه کارت، پنجاه
 * اشتراکِ کوئری یعنی پنجاه بار محاسبه‌ی همان یک فهرست. صفحه یک‌بار صدا می‌زند
 * و به دکمه‌ها پاس می‌دهد.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { favoriteRepo } from '@/core/data/miscRepo';
import { useSession } from '@/core/session';
import { t } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { trackFunnel } from '@/core/lib/analytics';
import {
  favoriteKey, favoriteKeys, type FavoriteKind, type FavoriteSnapshot,
} from '@/core/lib/favoriteOrder';
import { HeartIcon } from '@/ui/icons';
import { useAccountGate } from '@/features/signup/SignupGate';

export interface Favorites {
  /** مجموعه‌ی **زنده** — دکمه باید همان لحظه پر شود. برای ترتیب، عکسِ لحظه‌ای. */
  keys: FavoriteSnapshot;
  /** دادهٔ نشان‌شده‌ها رسیده و همین الان در حالِ گرفتن نیست. */
  settled: boolean;
  isFav: (kind: FavoriteKind, id: string) => boolean;
  toggle: (kind: FavoriteKind, id: string) => void;
}

export function useFavorites(): Favorites {
  // شناسه از خودِ نشست می‌آید، نه از پروفایل: پروفایل چند لحظه دیرتر از نشست
  // می‌رسد و در آن فاصله دکمه، کاربرِ واردشده را به صفحه‌ی ورود می‌فرستاد.
  // `favorites.user_id` هم دقیقاً همان `auth.uid()` است که RLS می‌خواهد.
  const { session, loading } = useSession();
  const qc = useQueryClient();
  const gate = useAccountGate();

  const q = useQuery({
    queryKey: ['favorites'], queryFn: favoriteRepo.list, enabled: !!session,
  });
  // `enabled: false` کشِ حسابِ قبلی را پاک نمی‌کند — بعد از خروج نشان‌ها می‌ماند.
  const keys = React.useMemo(
    () => favoriteKeys(session ? q.data ?? [] : []), [session, q.data]);
  const isFav = (kind: FavoriteKind, id: string) => keys.has(favoriteKey(kind, id));

  const set = useMutation({
    mutationFn: (a: { kind: FavoriteKind; id: string }) =>
      favoriteRepo.set(a.kind, a.id, !isFav(a.kind, a.id), session!.user.id),
    onSuccess: (_d, a) => {
      // قیف (WHOSUP-291): نشان کردنِ رخداد همان «اعلامِ علاقه» است.
      if (a.kind === 'event') {
        trackFunnel('rsvp', {
          id: String(a.id), source: 'discover', response: isFav(a.kind, a.id) ? 'uninterested' : 'interested',
        });
      }
      return qc.invalidateQueries({ queryKey: ['favorites'] });
    },
    onError: (e) => reportError('web.discover.favorite', e),
  });

  return {
    keys,
    settled: q.isSuccess && !q.isFetching,
    isFav,
    toggle: (kind, id) => {
      // ترتیبِ این سه شرط تجربی است، نه سلیقه‌ای. `loading` تا رسیدنِ **پروفایل**
      // روشن می‌ماند، ولی نشست چند صدم ثانیه زودتر می‌نشیند — و کاربر توی همان
      // فاصله کلیک می‌کند. پس اول نشست: اگر هست، کار همین حالا انجام می‌شود.
      if (session) { set.mutate({ kind, id }); return; }
      // نشستی نیست ولی هنوز هم معلوم نیست: نه ذخیره، نه فرستادن به صفحه‌ی ورود.
      if (loading) return;
      // مهمانِ واقعی. کشف برای او هم باز است، ولی نشان جایی برای ذخیره شدن
      // ندارد — برگه‌ی ثبت‌نام با دلیلش (WHOSUP-308)، و برگشت به همین صفحه.
      gate('save', () => undefined);
    },
  };
}

/**
 * خودِ دکمه. تو خالی یعنی نشان‌نشده، پُر یعنی نشان‌شده — همان زبانِ گوشی.
 *
 * روی کارت‌های فهرست، این دکمه **بیرونِ** لینکِ کارت می‌نشیند و با CSS رویش
 * می‌آید: دکمه داخلِ `<a>` هم HTMLِ نامعتبر است هم یعنی هر کلیک، هم‌زمان
 * صفحه را باز می‌کند.
 */
export function FavButton({ fav, kind, id, className }: {
  fav: Favorites; kind: FavoriteKind; id: string; className?: string;
}) {
  const on = fav.isFav(kind, id);
  const label = on ? t('favoriteRemove') : t('favoriteAdd');
  return (
    <button type="button" className={`favbtn${on ? ' on' : ''}${className ? ` ${className}` : ''}`}
      aria-pressed={on} aria-label={label} title={label}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); fav.toggle(kind, id); }}>
      <HeartIcon size={18} filled={on} />
    </button>
  );
}
